import { NavLink } from 'react-router-dom';
import {
  SquaresFour,
  PenNib,
  Layout,
  ChartLine,
  Globe,
  Gear,
  CaretUpDown,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Logo } from '@/components/brand/Logo';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils/cn';
import { portfolioApi } from '@/lib/api/portfolio';
import { tenantApi } from '@/lib/api/tenant';
import { queryKeys } from '@/lib/api/queryKeys';
import { portfolioLabel, publicPortfolioUrl } from '@/lib/config';

interface NavItem {
  to: string;
  label: string;
  icon: typeof SquaresFour;
}

const NAV: ReadonlyArray<NavItem> = [
  { to: '/app', label: 'Dashboard', icon: SquaresFour },
  { to: '/app/editor', label: 'Editor', icon: PenNib },
  { to: '/app/templates', label: 'Templates', icon: Layout },
  { to: '/app/analytics', label: 'Analytics', icon: ChartLine },
  { to: '/app/domains', label: 'Domains', icon: Globe },
  { to: '/app/settings', label: 'Settings', icon: Gear },
];

export function Sidebar() {
  const tenantQuery = useQuery({
    queryKey: queryKeys.tenant,
    queryFn: () => tenantApi.get(),
    staleTime: 60_000,
  });
  const portfolioQuery = useQuery({
    queryKey: queryKeys.portfolio,
    queryFn: () => portfolioApi.get(),
    staleTime: 60_000,
  });
  const sub = tenantQuery.data?.subdomain;
  const subdomain = sub ? portfolioLabel(sub) : portfolioLabel('your-site');
  const isPublished = Boolean(portfolioQuery.data?.publishedAt);
  const liveUrl = sub ? publicPortfolioUrl(sub) : '';

  return (
    <aside className="relative z-20 flex h-full w-[260px] shrink-0 flex-col justify-between border-r border-line bg-board">
      <div className="px-5 py-6">
        <div className="mb-10 px-2">
          <Logo />
        </div>

        <button
          type="button"
          className="group mb-8 flex w-full flex-col gap-0.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-rail"
        >
          <span className="pl-1 text-[10px] font-bold uppercase tracking-widest text-ink-faint">
            Workspace
          </span>
          <div className="flex items-center justify-between pl-1 pr-2">
            <span className="text-sm font-medium">Personal Site</span>
            <CaretUpDown className="text-ink-faint transition-colors group-hover:text-ink" size={14} />
          </div>
        </button>

        <nav className="space-y-1 pl-2 pr-3" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/app'}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-rail text-ink'
                    : 'text-ink-muted hover:bg-rail/60 hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden
                    className={cn(
                      'absolute -left-3 top-1/2 h-[18px] w-[3px] -translate-y-1/2 rounded-r bg-ink transition-transform',
                      isActive ? 'scale-y-100' : 'scale-y-0',
                    )}
                  />
                  <item.icon
                    size={18}
                    weight="regular"
                    className={cn(
                      'shrink-0',
                      isActive ? 'text-ink' : 'text-ink-faint group-hover:text-ink',
                    )}
                  />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="px-5 pb-6">
        <Card className="p-4" elevation="card">
          <p className="mb-1 flex items-center justify-between text-xs text-ink-muted">
            {isPublished ? 'Live' : 'Draft'}
            <span
              className={cn(
                'inline-flex h-2 w-2 rounded-full',
                isPublished ? 'bg-emerald-500' : 'bg-ink-faint',
              )}
              aria-hidden
            />
          </p>
          <div className="truncate text-sm font-medium" title={subdomain}>
            {subdomain}
          </div>
          {isPublished && liveUrl ? (
            <a
              href={liveUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail"
            >
              View site <ArrowSquareOut size={12} />
            </a>
          ) : null}
        </Card>
      </div>
    </aside>
  );
}
